// TemplateNavigationButtons.js
import React, { useState ,useEffect  } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { setShouldCreate, resetToDefaults } from "../../../redux/Slice/Templates/templateCreationSlice";
import { resetActiveStep, clearEditState } from "../../../redux/Slice/Templates/templateSlice";

const TemplateNavigationButtons = ({ handleNext, handleBack }) => {
  const { accountid } = useParams();
  const { activeStep, steps } = useSelector((state) => state.template);
  const { fieldsFlag, fieldsErrorMsg, status: createStatus, error: createError, editMode } = useSelector(
    (state) => state.templateCreation
  );
  // Edit submissions go through templateSlice's updateTemplate thunk instead of
  // templateCreationSlice's createTemplate — see TemplateSubmission.jsx.
  const updateStatus = useSelector((state) => state.template.updateStatus);
  const updateError = useSelector((state) => state.template.updateError);
  const status = editMode ? updateStatus : createStatus;
  const error = editMode ? updateError : createError;
  const isLastStep = activeStep === steps.length - 1;
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    // Only trigger on last step and when status changes to succeeded/failed
    if (
      activeStep === steps.length - 1 &&
      (status === "succeeded" || status === "failed")
    ) {
      if (status === "succeeded") {
        setSnackbarMsg(editMode ? "Template updated successfully!" : "Template created successfully!");
        setSnackbarSeverity("success");
        // Only redirect on success
        const timer = setTimeout(() => {
          setSnackbarOpen(false);
          dispatch(resetToDefaults());
          dispatch(resetActiveStep());
          dispatch(clearEditState());
          navigate(`/home/${accountid}/templates`);
        }, 2000);
        return () => clearTimeout(timer);
      } else {
        // Display user-friendly error message but DON'T redirect
        const errorMessage = typeof error === "string"
          ? error
          : error?.userFriendlyMessage ||
            error?.message ||
            error?.details?.error?.error_user_msg ||
            "Unknown error occurred";
        setSnackbarMsg(`Template ${editMode ? "update" : "creation"} failed: ${errorMessage}`);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        // Auto-hide snackbar after 5 seconds but don't redirect
        const timer = setTimeout(() => {
          setSnackbarOpen(false);
        }, 5000);
        return () => clearTimeout(timer);
      }
    }
  }, [status, error, dispatch, navigate, accountid, activeStep, steps.length, editMode]);
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleNextClick = () => {
    // If user is on step 2 but hasn't filled mandatory fields
    if (activeStep === 1 && !fieldsFlag) {
      setOpenDialog(true);
      return;
    }

    // If this is the last step => "Finish"
    if (isLastStep) {
      // Guard against a second create/update request firing while the
      // first is still in flight (e.g. a fast double-click before the
      // button's disabled state applies) — Meta's create-template call
      // isn't idempotent, so a duplicate request for the same
      // name+language gets rejected even though the first one succeeded.
      if (status === "loading") return;
      // Flip the shouldCreate flag to true
      dispatch(setShouldCreate(true));
    } else {
      // Otherwise, just go to next step
      handleNext();
    }
  };

  // Discard abandons whatever's in progress (a fresh create OR an in-flight
  // edit) — reset the same three slices of state the success flow resets
  // after a real submission, so the next "Create Template" click starts
  // clean instead of resuming this discarded draft's header/body/buttons/
  // carousel data, step, and editMode.
  const handleDiscard = () => {
    dispatch(resetToDefaults());
    dispatch(resetActiveStep());
    dispatch(clearEditState());
    navigate(`/home/${accountid}/templates`);
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", sm: "95%", md: "90%", lg: "80%", xl: "80%" },
        display: "flex",
        justifyContent: "space-between",
        padding: { xs: "8px", sm: "10px", md: "12px", lg: "1%", xl: "1%" },
        backgroundColor: (theme) => theme.palette.background.paper,
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0px -2px 10px rgba(0, 0, 0, 0.2)"
            : "0px -2px 10px rgba(0, 0, 0, 0.1)",
        borderRadius: "8px",
        gap: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 2 },
      }}
    >
      {activeStep === 0 || (editMode && activeStep === 1) ? (
        // In edit mode step 1 (category picker) is skipped entirely, so "Back" from
        // step 2 has nowhere sensible to go — going there would also reset editMode
        // via setSelectedCategory's full-state reset. Discard instead.
        <Button
          onClick={handleDiscard}
          variant="outlined"
          sx={{
            borderColor: (theme) => theme.palette.divider,
            color: (theme) => theme.palette.text.primary,
            fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "14px" },
            padding: { xs: "6px 12px", sm: "8px 16px", md: "10px 20px", lg: "10px 20px", xl: "10px 20px" },
            "&:hover": {
              borderColor: (theme) => theme.palette.primary.main,
            },
          }}
        >
          Discard
        </Button>
      ) : (
        <Button
          variant="outlined"
          onClick={handleBack}
          sx={{
            borderColor: (theme) => theme.palette.divider,
            color: (theme) => theme.palette.text.primary,
            fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "14px" },
            padding: { xs: "6px 12px", sm: "8px 16px", md: "10px 20px", lg: "10px 20px", xl: "10px 20px" },
            "&:hover": {
              borderColor: (theme) => theme.palette.primary.main,
            },
          }}
        >
          Back
        </Button>
      )}

      <Button
        variant="contained"
        onClick={handleNextClick}
        disabled={isLastStep && status === "loading"}
        sx={{
          backgroundColor: (theme) => theme.palette.primary.main,
          fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "14px" },
          padding: { xs: "6px 12px", sm: "8px 16px", md: "10px 20px", lg: "10px 20px", xl: "10px 20px" },
          "&:hover": {
            backgroundColor: (theme) => theme.palette.primary.dark,
          },
          "&.Mui-disabled": {
            backgroundColor: (theme) => theme.palette.action.disabledBackground,
            color: (theme) => theme.palette.action.disabled,
          },
        }}
      >
        {isLastStep
          ? status === "loading"
            ? editMode
              ? "Updating..."
              : "Creating..."
            : "Finish"
          : "Next"}
      </Button>

      {/* Validation Error Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
            backgroundColor: (theme) => theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: "bold",
            fontSize: "20px",
            padding: "20px 24px 16px",
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          <Alert severity="error" sx={{ p: 0, backgroundColor: 'transparent' }}>
            Required Fields Missing
          </Alert>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: "24px",
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please fill in all required fields before proceeding:
          </Typography>

          <Alert severity="error" sx={{ mb: 2 }}>
            <Box sx={{ whiteSpace: 'pre-line' }}>
              {fieldsErrorMsg}
            </Box>
          </Alert>

          <Typography variant="body2" color="textSecondary">
            All fields marked with an asterisk (*) are required and must be completed before you can continue to the next step.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            padding: "16px 24px 20px",
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          <Button
            onClick={handleCloseDialog}
            variant="contained"
            sx={{
              backgroundColor: (theme) => theme.palette.primary.main,
              "&:hover": {
                backgroundColor: (theme) => theme.palette.primary.dark,
              },
            }}
          >
            OK, I'll Fix It
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for template creation result */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={snackbarSeverity === "success" ? 2000 : 5000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%", maxWidth: 400 }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TemplateNavigationButtons;
